/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { useEffect, useState, type ReactNode, useMemo, useRef, useContext } from 'react';

import {
	CustomerPortalContext,
	CustomerPortalAction,
	type CustomerPortalAccount,
	type CustomerPortalActionOptions,
	type CustomerPortalCompletedFlowType,
	type CustomerPortalIntegrationType,
	type CustomerPortalMessage,
	type emitCustomerPortalEventArgs,
	type Subscription
} from '../../contexts/CustomerPortalContext';
import { isObject } from 'lodash';
import { getFeaturesFromToken } from '../../utils/getFeaturesFromToken/getFeaturesFromToken';
import { displayToast } from '../ToastProvider/displayToast';
import { getLicenseKeyFromUserData } from '../../utils/getLicenseKeyFromUserData/getLicenseKeyFromUserData';
import { jwtDecode } from 'jwt-decode';
import { GTMContext } from '../../contexts/GTMContext';
import {
	GTM_EVENT_LOG_IN,
	GTM_EVENT_LOGGED_IN,
	GTM_EVENT_SIGN_UP,
	GTM_EVENT_SIGNED_UP,
	GTM_EVENT_SIGNED_OUT
} from '../../constants/GTMEventNames';
import { CUSTOMER_PORTAL_USER_DATA_IFRAME_ID } from '../../constants/identifiers';

type CustomerPortalError = { error: unknown };

type CustomerPortalEventData = {
	isCustomerPortalEvent?: boolean;
	type: 'user-data' | 'signed-out' | 'completed-flow' | 'close' | 'error';
	payload: CustomerPortalAccount | CustomerPortalCompletedFlowType | CustomerPortalError;
};

type PostMessageEvent = {
	data: unknown | CustomerPortalEventData;
	origin: string;
};

export function CustomerPortalIntegration( { children }: { children: ReactNode } ): ReactNode {
	const { triggerGTMEvent } = useContext( GTMContext );

	// @ts-expect-error variable is injected by vite.
	const customerPortalUrl = CUSTOMER_PORTAL_URL;
	const routes = useMemo( () => ( {
		LOG_IN: customerPortalUrl + '/widgets/signin?frameAncestor=builder',
		SIGN_UP: customerPortalUrl + '/widgets/signup?frameAncestor=builder',
		USER_DATA: customerPortalUrl + '/widgets/user-data?frameAncestor=builder'
	} ), [ customerPortalUrl ] );

	const onSuccessRef = useRef<null | ( () => void )>( null );
	const [ flow, setFlow ] = useState<string | null>( null );
	const [ userData, setUserData ] = useState<CustomerPortalIntegrationType[ 'customerPortalAccount' ]>( null );
	const firstSubscription = userData?.subscriptions?.at( 0 );
	const featuresAvailableInPlan = useMemo( () => getFeaturesFromToken( userData ), [ userData ] );
	const decodedLicenseKey = useMemo( () => getDecodedLicenseKey( userData ), [ userData ] );

	useEffect( () => {
		if ( flow ) {
			document.body.style.overflow = 'hidden';

			return;
		}

		document.body.style.overflow = '';
	}, [ flow ] );

	function onSuccess() {
		displayToast( {
			message: 'You have logged in successfully.',
			variant: 'success'
		} );
	}

	function emitCustomerPortalEvent( { type, stage }: emitCustomerPortalEventArgs ): void {
		// @ts-expect-error variable is injected by vite.
		const customerPortalUrl = CUSTOMER_PORTAL_URL;

		const userDataIframe = document.getElementById( CUSTOMER_PORTAL_USER_DATA_IFRAME_ID ) as HTMLIFrameElement;

		const message: CustomerPortalMessage = type === 'sign-out' ?
			{ isBuilderEvent: true, type } :
			{ isBuilderEvent: true, type, payload: { stage } };

		userDataIframe?.contentWindow?.postMessage( message, customerPortalUrl );
	}

	useEffect( () => {
		const handleMessage = ( event: PostMessageEvent ) => {
			const { origin, data } = event;

			if ( origin !== customerPortalUrl ) {
				return;
			}

			if ( !isSpecifiedObject( data ) ) {
				return;
			}

			if ( !isCustomerPortalEvent( data ) ) {
				return;
			}

			const { type, payload } = data;

			switch ( type ) {
				case 'user-data': {
					const customerPortalAccount = payload as CustomerPortalAccount;

					setUserData( customerPortalAccount );

					if ( onSuccessRef.current ) {
						onSuccessRef.current();
						onSuccessRef.current = null;
					}

					break;
				}

				case 'signed-out': {
					triggerGTMEvent( { event: GTM_EVENT_SIGNED_OUT } );
					setUserData( null );

					if ( onSuccessRef.current ) {
						onSuccessRef.current();
						onSuccessRef.current = null;
					}

					break;
				}

				case 'completed-flow': {
					const flowType = ( payload as CustomerPortalCompletedFlowType ).flow;

					if ( flowType === 'signin' ) {
						triggerGTMEvent( { event: GTM_EVENT_LOGGED_IN } );
					}

					if ( flowType === 'signup' ) {
						triggerGTMEvent( { event: GTM_EVENT_SIGNED_UP } );
					}

					setFlow( null );
					break;
				}

				case 'close': {
					setFlow( null );
					onSuccessRef.current = null;
					break;
				}

				case 'error': {
					const errorPayload = payload as CustomerPortalError;

					setFlow( null );
					displayToast( { message: String( errorPayload.error ), variant: 'error' } );

					onSuccessRef.current = null;
					break;
				}

				default:
					break;
			}
		};

		window.addEventListener( 'message', handleMessage );

		return () => {
			window.removeEventListener( 'message', handleMessage );
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ customerPortalUrl, userData ] );

	return (
		<CustomerPortalContext.Provider value={ {
			customerPortalAccount: userData,
			isLoggedIn: isLoggedIn( userData ),
			isTrialActive: isLoggedIn( userData ) ? isTrialActive( firstSubscription ) : false,
			hasSelfHostedAccess: hasSelfHostedAccess( decodedLicenseKey ),
			hasCloudAccess: hasCloudAccess( decodedLicenseKey ),
			hasOldGPLAccess: hasOldGPLAccess( userData, decodedLicenseKey ),
			plan: firstSubscription?.details.plan ?? undefined,
			featuresAvailableInPlan,
			emitCustomerPortalEvent,
			getCustomerPortalUrl: () => customerPortalUrl,
			getUpgradePlanUrl: ( organizationId, subscriptionId ) => {
				const organizationRootUrl = `${ customerPortalUrl }/organizations/${ organizationId }`;

				if ( !subscriptionId ) {
					return organizationRootUrl;
				}

				return `${ organizationRootUrl }/management/${ subscriptionId }`;
			},
			performCustomerPortalAction: ( action, options? ) => {
				if ( action === CustomerPortalAction.LOG_IN ) {
					setFlow( routes.LOG_IN );
					triggerGTMEvent( { event: GTM_EVENT_LOG_IN } );
					onSuccessRef.current = () => {
						options?.onSuccess?.();
						onSuccess();
					};
				}

				if ( action === CustomerPortalAction.SIGN_UP ) {
					const optionsTyped = options as CustomerPortalActionOptions[ CustomerPortalAction.SIGN_UP ];
					setFlow( routes.SIGN_UP );
					triggerGTMEvent( { event: GTM_EVENT_SIGN_UP, option: optionsTyped.variant } );
					onSuccessRef.current = () => {
						options?.onSuccess?.();
						onSuccess();
					};
				}

				if ( action === CustomerPortalAction.LOG_OUT ) {
					emitCustomerPortalEvent( { type: 'sign-out' } );

					onSuccessRef.current = () => {
						options?.onSuccess?.();
					};
				}
			}
		} }
		>
			{ children }
			<div>
				<iframe
					id={ CUSTOMER_PORTAL_USER_DATA_IFRAME_ID }
					data-testid={ CUSTOMER_PORTAL_USER_DATA_IFRAME_ID }
					title="user-data"
					src={ routes.USER_DATA }
					style={ { position: 'fixed', width: 0, height: 0, border: 0 } }
				>
				</iframe>
				{ flow && (
					<iframe
						data-testid={ 'customer-portal-flow-iframe' }
						title="widget"
						src={ flow }
						style={ {
							position: 'fixed',
							top: 0,
							left: 0,
							width: '100vw',
							height: '100vh',
							border: 0,
							zIndex: 10000
						} }
					>
					</iframe>
				) }
			</div>
		</CustomerPortalContext.Provider>
	);
}

function isCustomerPortalEvent( data: CustomerPortalEventData ) {
	return 'isCustomerPortalEvent' in data && data.isCustomerPortalEvent;
}

function isSpecifiedObject( data: PostMessageEvent[ 'data' ] ): data is CustomerPortalEventData {
	return isObject( data ) && !Array.isArray( data );
}

function isTrialActive( subscription: Subscription | undefined ) {
	if ( !subscription?.details?.trial ) {
		return false;
	}

	const date = subscription.details.trial.until;

	return new Date() < new Date( date );
}

type DecodedLicenseKey = { distributionChannel: Array<string> | string };

function getDecodedLicenseKey( userData: CustomerPortalAccount | null ) {
	const licenseKey = getLicenseKeyFromUserData( userData, 'development' );

	if ( !licenseKey ) {
		return null;
	}

	return jwtDecode<DecodedLicenseKey>( licenseKey );
}

function hasSelfHostedAccess( licenseKey: DecodedLicenseKey | null ) {
	if ( !licenseKey ) {
		return false;
	}

	const distributionChannel = licenseKey.distributionChannel;

	if ( !distributionChannel ) {
		return true;
	}

	return distributionChannel === 'sh' || distributionChannel.includes( 'sh' );
}

function hasCloudAccess( licenseKey: DecodedLicenseKey | null ) {
	if ( !licenseKey ) {
		return false;
	}

	const distributionChannel = licenseKey.distributionChannel;

	if ( !distributionChannel ) {
		return false;
	}

	return distributionChannel === 'cloud' || distributionChannel.includes( 'cloud' );
}

function isLoggedIn( userData: CustomerPortalAccount | null ) {
	return !!userData;
}

function hasOldGPLAccess( userData: CustomerPortalAccount | null, licenseKey: DecodedLicenseKey | null ) {
	return isLoggedIn( userData ) && !licenseKey;
}
